import { type CSSProperties, type ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { type BrandPattern, formatEnglishToken, isMichromaBrand } from '@/constants/brandPattern';
import { useBrandPattern } from '@/hooks/useBrandPattern';

const WORD_LEN = 5;

type Phase = 'introBig' | 'introShrink' | 'ready' | 'lineIn' | 'flying' | 'done';

const ENGLISH_LINE_LETTER_SPACING = '0.1em';
const ENGLISH_LINE_TEXT_SHADOW = '0 1px 10px rgba(0,0,0,0.3)';

type TagPart =
  | { type: 'word'; text: string; index: 0 | 1 | 2 | 3 | 4 }
  | { type: 'amp' }
  | { type: 'space' };

const TAG_PARTS: TagPart[] = [
  { type: 'word', text: 'Computer', index: 0 },
  { type: 'space' },
  { type: 'word', text: 'Added', index: 1 },
  { type: 'space' },
  { type: 'word', text: 'Development', index: 2 },
  { type: 'space' },
  { type: 'amp' },
  { type: 'space' },
  { type: 'word', text: 'Engineering', index: 3 },
  { type: 'space' },
  { type: 'word', text: 'Co.,ltd.', index: 4 },
];

function englishHeadBase(p: BrandPattern): CSSProperties {
  return {
    color: '#FFFFFF',
    fontFamily: p.fontFamily,
    fontWeight: p.englishHeadWeight,
    letterSpacing: '0.04em',
    lineHeight: 1.15,
    display: 'inline-block',
  };
}

function englishBodyBase(p: BrandPattern): CSSProperties {
  return {
    fontFamily: p.fontFamily,
    fontWeight: p.englishBodyWeight,
    fontSize: '0.9em',
    letterSpacing: ENGLISH_LINE_LETTER_SPACING,
    display: 'inline',
  };
}

function appendEnglishLineToDomNode(host: HTMLElement, p: BrandPattern) {
  host.replaceChildren();
  for (const part of TAG_PARTS) {
    if (part.type === 'space') {
      host.appendChild(document.createTextNode(' '));
      continue;
    }
    if (part.type === 'amp') {
      host.appendChild(document.createTextNode(' & '));
      continue;
    }
    const { head: h, rest: r } = formatEnglishToken(part.text, p.englishCasing);
    const wrap = document.createElement('span');
    const head = document.createElement('span');
    head.textContent = h;
    Object.assign(head.style, {
      color: '#fff',
      fontFamily: p.fontFamily,
      fontWeight: String(p.englishHeadWeight),
      letterSpacing: '0.04em',
      lineHeight: '1.15',
      fontSize: '1em',
      display: 'inline-block',
    });
    const rest = document.createElement('span');
    rest.textContent = r;
    Object.assign(rest.style, {
      fontFamily: p.fontFamily,
      fontWeight: String(p.englishBodyWeight),
      fontSize: '0.9em',
      letterSpacing: ENGLISH_LINE_LETTER_SPACING,
      display: 'inline',
    });
    wrap.appendChild(head);
    wrap.appendChild(rest);
    host.appendChild(wrap);
  }
}

function EnglishLineStaticParts({ pattern }: { pattern: BrandPattern }) {
  return (
    <>
      {TAG_PARTS.map((part, keyIdx) => {
        if (part.type === 'space') {
          return (
            <span key={keyIdx}>
              {' '}
            </span>
          );
        }
        if (part.type === 'amp') {
          return <span key={keyIdx}> & </span>;
        }
        const { head, rest } = formatEnglishToken(part.text, pattern.englishCasing);
        return (
          <span key={keyIdx}>
            <span style={englishHeadBase(pattern)}>{head}</span>
            <span style={englishBodyBase(pattern)}>{rest}</span>
          </span>
        );
      })}
    </>
  );
}

/** ロード直後: 大きい英名を表示する時間 */
const INTRO_BIG_MS = 1200;
/**
 * 英名: FLIP（transform+scale）で 1 本の補間。width/font 直接補間は改行でガタつくため使わない。
 * 入り速め・奥でしっとり減速（中間は一つの山、キーフレームは2点のまま）
 */
const INTRO_SHRINK_MS = 1200;
const INTRO_SHRINK_EASE = 'cubic-bezier(0.18, 0.7, 0.15, 0.99)';

/** 英語行が揃ってから飛び出し開始まで */
const BASE_DELAY_MS = 520;
/** 1文字の飛行時間（一本のイージングで終端まで） */
const FLIGHT_MS = 1360;
/**
 * 次の文字の開始を前の文字の飛行の約半分のタイミングに重ねる（着地前に次が流れ始める）
 */
const FLIGHT_STAGGER_OVERLAP = 0.47;
const STAGGER_MS = Math.round(FLIGHT_MS * FLIGHT_STAGGER_OVERLAP);
/** ホールドなしの流れ用イージング */
const FLIGHT_EASE = 'cubic-bezier(0.33, 0.06, 0.18, 1)';
/** 元の文字: 飛び出し直前の溜め（短めで重なりに合わせる） */
const CHARGE_LEAD_MS = 100;
/** 元の文字: 強調を保つ時間 */
const SOURCE_GLOW_MS = 420;
/** ズーム完了後の1回転 */
const SPIN_MS = 600;
const SPIN_EASE = 'cubic-bezier(0.28, 0, 0.1, 1)';

function usePrefersReducedMotion(): boolean {
  const [p, setP] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setP(mq.matches);
    const h = () => setP(mq.matches);
    mq.addEventListener('change', h);
    return () => mq.removeEventListener('change', h);
  }, []);
  return p;
}

type Ghost = { id: string; i: number; left: number; top: number; letter: string; fontSize: string };

type FlipData = { start: DOMRect[]; end: DOMRect[] };

export default function HeroCadecBuild({ companyName }: { companyName: ReactNode }) {
  const { pattern } = useBrandPattern();
  const letters = pattern.wordmarkChars;
  const michroma = isMichromaBrand(pattern);
  const reduce = usePrefersReducedMotion();
  const letterEls = useRef<(HTMLSpanElement | null)[]>([]);
  const acronymEls = useRef<(HTMLSpanElement | null)[]>([]);
  const introBigRef = useRef<HTMLParagraphElement | null>(null);
  const enTargetRef = useRef<HTMLParagraphElement | null>(null);
  const [phase, setPhase] = useState<Phase>(reduce ? 'done' : 'introBig');
  const [ghosts, setGhosts] = useState<Ghost[] | null>(null);
  const [chargingFrom, setChargingFrom] = useState<number | null>(null);
  const [launchingFrom, setLaunchingFrom] = useState<number | null>(null);
  const flipDataRef = useRef<FlipData | null>(null);
  const flightInitRef = useRef(false);
  const waaBeganRef = useRef(false);
  const introShrinkInitRef = useRef(false);
  const [introLandFlash, setIntroLandFlash] = useState(false);
  const introLandFlashed = useRef(false);

  const setLetterRef = (i: number) => (el: HTMLSpanElement | null) => {
    letterEls.current[i] = el;
  };
  const setAcronymRef = (i: number) => (el: HTMLSpanElement | null) => {
    acronymEls.current[i] = el;
  };

  useLayoutEffect(() => {
    if (reduce) return;
    let cancel = false;
    void (async () => {
      if (document.fonts?.ready) {
        try {
          await document.fonts.ready;
        } catch {
          /* ignore */
        }
      }
      if (cancel) return;
    })();
    return () => {
      cancel = true;
    };
  }, [reduce]);

  useEffect(() => {
    if (reduce) return;
    if (phase !== 'introBig') return;
    const t = window.setTimeout(() => setPhase('introShrink'), INTRO_BIG_MS);
    return () => clearTimeout(t);
  }, [phase, reduce]);

  useEffect(() => {
    if (reduce) return;
    if (phase !== 'lineIn' || introLandFlashed.current) return;
    introLandFlashed.current = true;
    setIntroLandFlash(true);
    const t = window.setTimeout(() => setIntroLandFlash(false), 900);
    return () => clearTimeout(t);
  }, [phase, reduce]);

  useLayoutEffect(() => {
    if (reduce) return;
    if (phase !== 'introShrink') {
      if (phase === 'done' || phase === 'introBig') introShrinkInitRef.current = false;
      return;
    }
    if (introShrinkInitRef.current) return;
    const a = introBigRef.current;
    const b = enTargetRef.current;
    if (!a || !b) {
      introShrinkInitRef.current = true;
      setPhase('ready');
      return;
    }
    const from = a.getBoundingClientRect();
    const bEl = b as HTMLParagraphElement;
    bEl.style.setProperty('transform', 'none');
    void bEl.offsetWidth;
    const to = b.getBoundingClientRect();
    bEl.style.removeProperty('transform');

    if (to.width < 1 || to.height < 1 || from.width < 1) {
      introShrinkInitRef.current = true;
      setPhase('ready');
      return;
    }
    introShrinkInitRef.current = true;
    a.style.visibility = 'hidden';

    const csb = window.getComputedStyle(b);
    const fs1 = csb.fontSize;

    const el = document.createElement('div');
    el.setAttribute('aria-hidden', 'true');
    el.className = 'pointer-events-none';
    el.style.setProperty('position', 'fixed');
    el.style.setProperty('z-index', '200');
    el.style.setProperty('box-sizing', 'border-box');
    el.style.setProperty('left', `${to.left}px`);
    el.style.setProperty('top', `${to.top}px`);
    el.style.setProperty('width', `${to.width}px`);
    el.style.setProperty('margin', '0');
    el.style.setProperty('color', '#fff');
    el.style.setProperty('text-align', 'center');
    el.style.setProperty('text-shadow', ENGLISH_LINE_TEXT_SHADOW);
    el.style.setProperty('line-height', csb.lineHeight);
    el.style.setProperty('overflow', 'visible');
    el.style.setProperty('backface-visibility', 'hidden');
    el.style.setProperty('white-space', (csb.whiteSpace || 'normal') as string);
    el.style.setProperty('padding', csb.padding);
    el.style.setProperty('font-family', csb.fontFamily);
    el.style.setProperty('font-size', fs1);
    el.style.setProperty('font-weight', csb.fontWeight);
    el.style.setProperty('font-style', csb.fontStyle);
    el.style.setProperty('letter-spacing', csb.letterSpacing);
    el.style.setProperty('will-change', 'transform');
    el.style.setProperty('transform-origin', '0 0');
    appendEnglishLineToDomNode(el, pattern);
    document.body.appendChild(el);
    const toClone = el.getBoundingClientRect();
    const scaleX = from.width / toClone.width;
    const scaleY = from.height / toClone.height;
    if (!Number.isFinite(scaleX) || !Number.isFinite(scaleY) || scaleX < 0.01 || scaleY < 0.01) {
      if (el.parentNode) el.remove();
      a.style.visibility = '';
      introShrinkInitRef.current = true;
      setPhase('ready');
      return;
    }
    const invX = from.left - toClone.left;
    const invY = from.top - toClone.top;
    const t0 = `translate3d(${invX}px, ${invY}px, 0) scale3d(${scaleX}, ${scaleY}, 1)`;
    el.style.setProperty('transform', t0);

    let anim: Animation | undefined;
    let cancelled = false;

    const cleanup = () => {
      if (el.parentNode) el.remove();
    };

    const finish = () => {
      if (cancelled) return;
      cleanup();
      setPhase('ready');
      introShrinkInitRef.current = false;
    };

    const fail = () => {
      if (cancelled) return;
      cleanup();
      setPhase('ready');
      introShrinkInitRef.current = false;
    };

    try {
      void el.offsetWidth;
      const kf0 = t0;
      const kf1 = 'translate3d(0, 0, 0) scale3d(1, 1, 1)';
      anim = el.animate(
        [
          { transform: kf0, easing: INTRO_SHRINK_EASE },
          { transform: kf1 },
        ],
        {
          duration: INTRO_SHRINK_MS,
          fill: 'forwards',
        },
      );
    } catch {
      fail();
      return;
    }

    void anim.finished
      .then(() => {
        if (!cancelled) finish();
      })
      .catch(() => {
        fail();
      });

    return () => {
      cancelled = true;
      try {
        anim?.cancel();
      } catch {
        /* ignore */
      }
      cleanup();
      a.style.visibility = '';
      introShrinkInitRef.current = false;
    };
  }, [phase, reduce, pattern]);

  useEffect(() => {
    if (reduce) return;
    if (phase !== 'ready') return;
    setPhase('lineIn');
  }, [phase, reduce]);

  useEffect(() => {
    if (reduce) return;
    if (phase !== 'lineIn') return;
    const t = window.setTimeout(() => {
      setPhase('flying');
    }, BASE_DELAY_MS);
    return () => clearTimeout(t);
  }, [phase, reduce]);

  /* 溜め（charging）→ 発光（launching）で「ここから」を段階的に強調。i=0 は同フレームで溜めが短い */
  useEffect(() => {
    if (reduce) return;
    if (phase !== 'flying' || !ghosts) return;
    const timers: ReturnType<typeof setTimeout>[] = [];
    for (let i = 0; i < 5; i += 1) {
      const tLaunch = i * STAGGER_MS;
      const tCharge = Math.max(0, tLaunch - CHARGE_LEAD_MS);
      timers.push(
        setTimeout(() => {
          setChargingFrom(i);
        }, tCharge),
      );
      timers.push(
        setTimeout(() => {
          setChargingFrom(null);
          setLaunchingFrom(i);
        }, tLaunch),
      );
      timers.push(
        setTimeout(() => {
          setLaunchingFrom((n) => (n === i ? null : n));
        }, tLaunch + SOURCE_GLOW_MS),
      );
    }
    return () => {
      for (const t of timers) clearTimeout(t);
      setChargingFrom(null);
      setLaunchingFrom(null);
    };
  }, [phase, ghosts, reduce]);

  useLayoutEffect(() => {
    if (reduce) return;
    if (phase !== 'flying') {
      if (
        phase === 'done' ||
        phase === 'lineIn' ||
        phase === 'ready' ||
        phase === 'introBig' ||
        phase === 'introShrink'
      ) {
        flightInitRef.current = false;
      }
      return;
    }
    if (flightInitRef.current) return;
    flightInitRef.current = true;

    const startRects = acronymEls.current.map((el) => (el ? el.getBoundingClientRect() : null));
    const endRects = letterEls.current.map((el) => (el ? el.getBoundingClientRect() : null));
    if (startRects.some((r) => !r) || endRects.some((r) => !r)) {
      flightInitRef.current = false;
      setPhase('done');
      return;
    }

    flipDataRef.current = { start: startRects as DOMRect[], end: endRects as DOMRect[] };
    const firstLetter = letterEls.current[0];
    const ghostFontSize = firstLetter ? getComputedStyle(firstLetter).fontSize : '3rem';
    setGhosts(
      [...letters].map((letter, i) => ({
        id: `cadec-fly-${i}`,
        i,
        left: (endRects[i] as DOMRect).left,
        top: (endRects[i] as DOMRect).top,
        letter,
        fontSize: ghostFontSize,
      })),
    );
  }, [phase, reduce, letters]);

  useLayoutEffect(() => {
    if (reduce) return;
    if (!ghosts || !flipDataRef.current) return;
    if (waaBeganRef.current) return;
    waaBeganRef.current = true;

    const { start, end } = flipDataRef.current;
    const animList: Animation[] = [];
    let cancelled = false;

    for (let i = 0; i < 5; i += 1) {
      const el = document.getElementById(ghosts[i].id);
      if (!el) continue;
      const s = start[i] as DOMRect;
      const e = end[i] as DOMRect;
      const dx = s.left - e.left;
      const dy = s.top - e.top;
      const sc = s.width / e.width;
      const startTf = `translate3d(${dx}px, ${dy}px, 0) scale3d(${sc}, ${sc}, 1)`;
      const endTf = 'translate3d(0, 0, 0) scale3d(1, 1, 1)';

      el.style.willChange = 'transform';
      el.style.backfaceVisibility = 'hidden';
      el.style.transformOrigin = 'top left';
      el.style.transform = startTf;

      const flip = el.animate(
        [
          { offset: 0, transform: startTf, easing: FLIGHT_EASE },
          { offset: 1, transform: endTf },
        ],
        {
          duration: FLIGHT_MS,
          delay: i * STAGGER_MS,
          fill: 'forwards',
        },
      );
      animList.push(flip);

      void flip.finished
        .then(() => {
          if (cancelled) return;
          const ox = e.width / 2;
          const oy = e.height / 2;
          el.style.transformOrigin = `${ox}px ${oy}px`;
          const spin = el.animate(
            [
              { transform: 'translate3d(0, 0, 0) scale3d(1, 1, 1) rotateY(0deg)' },
              { transform: 'translate3d(0, 0, 0) scale3d(1, 1, 1) rotateY(360deg)' },
            ],
            {
              duration: SPIN_MS,
              easing: SPIN_EASE,
              fill: 'forwards',
            },
          );
          animList.push(spin);
          spin.onfinish = () => {
            if (cancelled) return;
            el.style.willChange = '';
            el.style.backfaceVisibility = '';
            el.style.transform = '';
            el.style.removeProperty('transformOrigin');
          };
        })
        .catch(() => {
          /* flip がキャンセル */
        });
    }

    const lastFinish = (WORD_LEN - 1) * STAGGER_MS + FLIGHT_MS + SPIN_MS;
    const to = window.setTimeout(() => {
      if (cancelled) return;
      for (const a of animList) a.cancel();
      flipDataRef.current = null;
      setGhosts(null);
      waaBeganRef.current = false;
      flightInitRef.current = false;
      setChargingFrom(null);
      setLaunchingFrom(null);
      setPhase('done');
    }, lastFinish + 200);

    return () => {
      cancelled = true;
      clearTimeout(to);
      for (const a of animList) a.cancel();
      waaBeganRef.current = false;
    };
  }, [ghosts, reduce]);

  const safetyFired = useRef(false);
  useEffect(() => {
    if (reduce) return;
    if (phase === 'done') return;
    if (safetyFired.current) return;
    const t = window.setTimeout(() => {
      if (safetyFired.current) return;
      safetyFired.current = true;
      setGhosts(null);
      setChargingFrom(null);
      setLaunchingFrom(null);
      waaBeganRef.current = false;
      flightInitRef.current = false;
      setPhase('done');
    }, 32000);
    return () => clearTimeout(t);
  }, [phase, reduce]);

  const useTaggedText =
    reduce ||
    phase === 'ready' ||
    phase === 'lineIn' ||
    phase === 'flying' ||
    phase === 'done';
  const englishFaded = !reduce && (phase === 'introBig' || phase === 'introShrink' || phase === 'ready');
  const h1Visible = reduce || phase === 'done';
  const showBigEnglish = !reduce && (phase === 'introBig' || phase === 'introShrink');

  return (
    <>
      <div className="cadec-inline-cq relative z-20 mb-8 flex min-h-[clamp(6.5rem,18vh,12rem)] w-full max-w-5xl flex-col items-center justify-center">
        {showBigEnglish && (
          <p
            ref={introBigRef}
            className={`cadec-hero-intro absolute left-0 right-0 z-[2] mx-auto flex w-full justify-center px-2 text-center sm:px-3 ${
              michroma ? 'cadec-hero-intro--michroma' : ''
            }`}
            style={{
              top: '50%',
              transform: 'translateY(-50%)',
              lineHeight: 1.4,
              color: '#FFFFFF',
              textShadow: ENGLISH_LINE_TEXT_SHADOW,
              fontFamily: pattern.fontFamily,
              letterSpacing: ENGLISH_LINE_LETTER_SPACING,
              visibility: phase === 'introShrink' ? 'hidden' : 'visible',
            }}
          >
            <span className="inline-block w-full min-w-0 max-w-[min(100%,56rem)] text-balance sm:min-w-min md:whitespace-nowrap">
              <EnglishLineStaticParts pattern={pattern} />
            </span>
          </p>
        )}
        <h1
          className={`cadec-hero-h1 font-normal leading-none select-none ${michroma ? 'cadec-hero-h1--michroma' : ''}`}
          style={{
            color: '#FFFFFF',
            letterSpacing: pattern.wordmarkLetterSpacing,
            fontFamily: pattern.fontFamily,
            fontWeight: pattern.wordmarkWeight,
            textShadow: '0 2px 60px rgba(0,0,0,0.4)',
            opacity: h1Visible ? 1 : 0,
            transition: reduce ? undefined : 'opacity 0.5s ease',
          }}
        >
          {letters.map((ch, i) => (
            <span key={`${ch}-${i}`} ref={setLetterRef(i)} className="inline-block" style={{ width: 'auto' }}>
              {ch}
            </span>
          ))}
        </h1>
      </div>

      <div className="cadec-inline-cq mb-10 flex w-full max-w-4xl flex-col items-center gap-2.5 text-center">
        {companyName}
        <p
          ref={reduce ? undefined : enTargetRef}
          className={`cadec-hero-en font-light relative z-0 md:whitespace-nowrap ${michroma ? 'cadec-hero-en--michroma' : ''} ${
            introLandFlash && !reduce ? 'hero-cadec-intro-land-flash' : ''
          } ${
            reduce ? 'opacity-100' : englishFaded ? 'translate-y-3 opacity-0' : 'translate-y-0 opacity-100'
          }`}
          style={{
            color: '#FFFFFF',
            textShadow: ENGLISH_LINE_TEXT_SHADOW,
            letterSpacing: ENGLISH_LINE_LETTER_SPACING,
            fontFamily: pattern.fontFamily,
            ...(!reduce
              ? { transition: 'opacity 0.55s ease, transform 0.6s cubic-bezier(0.22, 0.9, 0.25, 1)' }
              : {}),
          }}
        >
          {reduce ? (
            <EnglishLineStaticParts pattern={pattern} />
          ) : useTaggedText ? (
            <>
              {TAG_PARTS.map((part, keyIdx) => {
                if (part.type === 'space') return ' ';
                if (part.type === 'amp') return ' & ';
                const { head, rest } = formatEnglishToken(part.text, pattern.englishCasing);
                const isBurst = launchingFrom === part.index;
                const isCharge = !isBurst && chargingFrom === part.index;
                return (
                  <span key={keyIdx}>
                    <span className="relative z-0 inline-block align-baseline">
                      {isCharge && !isBurst && (
                        <span
                          className="hero-cadec-ripple hero-cadec-ripple--charge"
                          key={`c-${keyIdx}`}
                          aria-hidden
                        />
                      )}
                      {isBurst && (
                        <>
                          <span className="hero-cadec-ripple hero-cadec-ripple--burst" key={`b1-${keyIdx}`} aria-hidden />
                          <span className="hero-cadec-ripple hero-cadec-ripple--burst2" key={`b2-${keyIdx}`} aria-hidden />
                        </>
                      )}
                      <span
                        ref={setAcronymRef(part.index)}
                        className={`relative z-[1] inline-block align-baseline ${
                          isBurst ? 'hero-cadec-acronym-flash' : ''
                        }`}
                        style={{
                          ...englishHeadBase(pattern),
                          color: phase === 'flying' ? 'transparent' : '#FFFFFF',
                          transform: isBurst ? 'scale(1.2)' : isCharge ? 'scale(1.08)' : 'scale(1)',
                          textShadow: isCharge ? '0 0 12px rgba(255,255,255,0.45)' : undefined,
                          transition: 'transform 0.4s cubic-bezier(0.42, 0, 0.58, 1), text-shadow 0.4s ease, color 0.15s ease',
                        }}
                      >
                        {head}
                      </span>
                    </span>
                    <span style={englishBodyBase(pattern)}>{rest}</span>
                  </span>
                );
              })}
            </>
          ) : (
            <EnglishLineStaticParts pattern={pattern} />
          )}
        </p>
      </div>

      {!reduce && ghosts
        ? createPortal(
            <div
              className="pointer-events-none"
              style={{
                position: 'fixed',
                inset: 0,
                zIndex: 100,
                perspective: '1000px',
                perspectiveOrigin: '50% 40%',
                transformStyle: 'preserve-3d',
              }}
              aria-hidden
            >
              {ghosts.map((g) => (
                <span
                  key={g.id}
                  id={g.id}
                  className="absolute left-0 top-0"
                  style={{
                    fontSize: g.fontSize,
                    color: '#FFFFFF',
                    fontFamily: pattern.fontFamily,
                    fontWeight: pattern.wordmarkWeight,
                    textShadow: '0 2px 60px rgba(0,0,0,0.4)',
                    letterSpacing: pattern.wordmarkLetterSpacing,
                    lineHeight: 1,
                    left: g.left,
                    top: g.top,
                    backfaceVisibility: 'visible',
                    transformStyle: 'preserve-3d',
                  }}
                >
                  {g.letter}
                </span>
              ))}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
